import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ROUTE_TYPE } from '../../Configuration'
import {
  COLUMN_NAMES,
  DueOutCategoryCount,
  DueOutDueDateCount,
  DueOutFetchArgs,
  DueOutPriorityCount,
  DueOutStatusCount,
} from '../../assets/ts/data/dueout/dueout'
import { STATUS_CODE } from '../../assets/ts/data/dueout/dueout-status-metadata'
import { InquiryFetchArgs } from '../../assets/ts/data/inquiry/inquiry'
import { defaultToEmptyString, twoDigits } from '../../assets/ts/formatting/formatting'
import { handleScroll } from '../../assets/ts/general/general'
import DashboardKPIs from '../../components/dashboard-kpis/dashboard-kpis'
import DueoutsTable from '../../components/table/dueouts-table'
import useDueOutCount from '../../hooks/dueout/use-dueout-count'
import useDueoutViews from '../../hooks/dueout/use-dueout-view'
import usePageAccumulator from '../../hooks/usePageAccumulator'
import { PreviousPageContext } from '../../store/previous-page-provider'
import { RoleContext } from '../../store/role-context-provider'
import { ALL_SELECT_VALUE, ALL_SELECT_VALUE_STRING } from '../../utils/constants/global-constants'
import { NORESULT } from '../../utils/constants/rhybus-config'
import ActionPendingChart from './charts/action-pending'
import ByCategoryChart from './charts/by-categories'
import TotalsByMonthChart from './charts/by-month'
import ByStatusChart from './charts/inquiries-by-status'
import ByPieChart from './charts/pie-chart'

interface IAdminDashBoardTabContent {
  allCounts?: any
  categories: any[]
  dashboardKPIs: {
    border?: string | undefined
    count: number | string
    kpicode: string
    title: string
  }[]
  dueOutCount: number
  dueOutPriorityCount: DueOutPriorityCount[]
  dueOutStatusCount: DueOutStatusCount[]
  dueoutCategoryCounts: DueOutCategoryCount[]
  inquiryCategoryCounts: any
  inquiryCustomer: any
  inquiryFilter: InquiryFetchArgs
  inquiryPending: any
  inquirySourceCSSCount: number
  inquirySourceCustomerCount: number
  mode: string
  morethanTenDaysAgoDueDateCount: number
  setInquiryFilter: any
  sevevenDaysAgoDueDateCount: number
  statusCounts: any
  tab: number
  tenDaysAgoDueDateCount: number
  threeDaysAgoDueDateCount: number
  totalByMonthCounts: any
}

const AdminDashBoardTabContent = ({
  categories,
  dashboardKPIs,
  dueOutCount,
  dueOutPriorityCount,
  dueOutStatusCount,
  dueoutCategoryCounts,
  inquiryCategoryCounts,
  inquiryCustomer,
  inquiryFilter,
  inquiryPending,
  inquirySourceCSSCount,
  inquirySourceCustomerCount,
  mode,
  morethanTenDaysAgoDueDateCount,
  setInquiryFilter,
  sevevenDaysAgoDueDateCount,
  statusCounts,
  tab,
  tenDaysAgoDueDateCount,
  threeDaysAgoDueDateCount,
  totalByMonthCounts,
}: IAdminDashBoardTabContent) => {
  const pendingActionCount =
    inquiryPending.inquiryPendingMoreThanTenDays +
    inquiryPending.inquiryPendingSevenDays +
    inquiryPending.inquiryPendingTenDays +
    inquiryPending.inquiryPendingThreeDays

  const customerActionCount =
    inquiryCustomer.inquiryCustomerMoreThanTenDays +
    inquiryCustomer.inquiryCustomerSevenDays +
    inquiryCustomer.inquiryCustomerTenDays +
    inquiryCustomer.inquiryCustomerThreeDays
  const statusCount =
    statusCounts.cssInquiryCount +
    statusCounts.leadershipInquiryCount +
    statusCounts.sectionChiefInquiryCount +
    statusCounts.supervisorInquiryCount +
    statusCounts.technicianInquiryCount +
    statusCounts.submittedInquiryCount +
    statusCounts.reopenedInquiryCount
  const initialDueOutFilter: DueOutFetchArgs = {
    CSSPASCODEFilter: -1,
    assignedComponentCodeFilter: tab,
    assignedToLocationFilter: '',
    categoryCodeFilter: ALL_SELECT_VALUE,
    hasClosedAndCanceled: false,
    isArchivedFilter: false,
    isAscending: false,
    isViewApplied: true,
    orderBy: COLUMN_NAMES.ItemModified,
    pascode: [],
    sectionCodeFilter: ALL_SELECT_VALUE,
    servicingByFilter: ALL_SELECT_VALUE_STRING,
    servicingMAJCOMCodeFilter: ALL_SELECT_VALUE,
    servicingOrganizationFilter: ALL_SELECT_VALUE_STRING,
    statusCodesOmittedFilter: [STATUS_CODE.SAVED],
    yearFilter: ALL_SELECT_VALUE,
  }
  const { setKPIClicked } = useContext(PreviousPageContext)
  const { initialInquiryAndDueoutFilter } = useContext(RoleContext)
  const UserRoleCodes: Partial<DueOutFetchArgs> = {
    userLoginRole: {
      CSSPASCODEFilter: initialInquiryAndDueoutFilter?.cssPASCODES,
      usersAssignedComponentCode: initialInquiryAndDueoutFilter?.assignedComponentCodeFilter,
      usersMajcomCodeFilter: initialInquiryAndDueoutFilter?.majcomCodeFilter,
      usersSectionCodeFilter: initialInquiryAndDueoutFilter?.sectionCodeFilter,
      usersServicingOrganizationFilter: initialInquiryAndDueoutFilter?.servicingOrganizationFilter,
      usersServicingOrganizationWithoutSectionFilter:
        initialInquiryAndDueoutFilter?.servicingOrganizationWithoutSectionFilter,
    },
  }
  const roleContext = useContext(RoleContext)
  const [pageNum, setPageNum] = useState(0)
  const [dueoutFilter, setDueoutFilter] = useState({ ...initialDueOutFilter, ...UserRoleCodes })
  const dueOutPaging: DueOutFetchArgs = {
    pageNumber: pageNum,
  }
  useEffect(() => {
    setDueoutFilter((prevFilter) => ({
      ...prevFilter,
      ...UserRoleCodes,
    }))
  }, [initialInquiryAndDueoutFilter])
  let categoryTotal = 0
  inquiryCategoryCounts.forEach((category) => (categoryTotal += category?.InquiryGUID_count))
  const { dueouts } = useDueoutViews({
    ...dueoutFilter,
    ...dueOutPaging,
  })
  const { dueOutCountValue: kpiFilteredDueOutCount } = useDueOutCount({
    ...dueoutFilter,
  })
  const accumulatedDueOuts = usePageAccumulator(pageNum, dueouts)
  const handleBottomScrollReached = () => {
    if (kpiFilteredDueOutCount > accumulatedDueOuts.length) {
      setPageNum((num) => num + 1)
    }
  }
  const onTopScrollReached = (e) => {
    console.log(e)
  }
  const getDueOutGUIDCount = (statusCode) => {
    const result = dueOutStatusCount.find(({ StatusCode }) => StatusCode === statusCode)
    return result ? result.DueOutGUID_count : 0
  }
  const getDueOutGUIDPriorityCount = (priority) => {
    const result = dueOutPriorityCount.find(({ Priority }) => Priority === priority)
    return result ? result.DueOutGUID_count : 0
  }
  
  const overdueCount = 
  threeDaysAgoDueDateCount + 
  sevevenDaysAgoDueDateCount + 
  tenDaysAgoDueDateCount + 
  morethanTenDaysAgoDueDateCount
  const dueDateResult = [
    {
      DueDate: '3 Days',
      DueOutGUID_count: threeDaysAgoDueDateCount,
    },
    {
      DueDate: '4 - 7 Days',
      DueOutGUID_count: sevevenDaysAgoDueDateCount,
    },
    {
      DueDate: '7 - 10 Days',
      DueOutGUID_count: tenDaysAgoDueDateCount,
    },
    {
      DueDate: 'More Than 10 Days',
      DueOutGUID_count: morethanTenDaysAgoDueDateCount,
    },
  ]
  return (
    <>
      <DashboardKPIs
        dashboardKPIs={dashboardKPIs}
        inquiryFilterProps={{ inquiryFilter, setInquiryFilter }}
        switchMode={mode}
        tab={tab}
      />

      <div className='row'>
        {!roleContext.isCSS && (
          <div
            className={
              mode === 'dueout'
                ? 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch d-none my-2'
                : 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch my-2'
            }
          >
            <div className='shadow card w-100'>
              <div className='d-flex flex-column card-body h-100'>
                <div className='d-flex justify-content-between align-items-center flex-wrap'>
                  <div className='d-flex mb-1 title-border'>
                    <h2
                      aria-label='Inquiries by Source'
                      className='poppins-bold font-14 title-color20'
                      tabIndex={0}
                    >
                      Inquiries by Source{' '}
                      <span className='badge bgcolor-primary border-radius3 px-2 py-1 font-12 whitetext poppins-semibold'>
                        {defaultToEmptyString(
                          twoDigits(inquirySourceCSSCount + inquirySourceCustomerCount)
                        )}
                      </span>
                    </h2>
                  </div>
                  <div className='ms-md-auto'>
                    {inquirySourceCSSCount + inquirySourceCustomerCount > 0 && (
                      <Link
                        className='links-over-underline font-12 links'
                        title='View All'
                        to={`${ROUTE_TYPE}inquiries`}
                      >
                        View All
                      </Link>
                    )}
                    {inquiryFilter.servicingByFilter !== '' && (
                      <Link
                        className='links-over-underline font-12 links mx-2 border-left1 ps-2'
                        onClick={() =>
                          setInquiryFilter({
                            ...inquiryFilter,
                            servicingByFilter: '',
                          })
                        }
                        title='Reset Filter'
                        to='#'
                      >
                        Reset Filter
                      </Link>
                    )}
                  </div>
                </div>
                <div className='min-h-200 w-100-important d-flex align-items-center justify-content-around'>
                  {inquirySourceCSSCount + inquirySourceCustomerCount > 0 ? (
                    <ByPieChart
                      inquirySourceCSSCount={inquirySourceCSSCount}
                      inquirySourceCustomerCount={inquirySourceCustomerCount}
                    />
                  ) : (
                    <div
                      aria-label={NORESULT}
                      className='subtitle-color2 montserratregular'
                      tabIndex={0}
                    >
                      {NORESULT}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Due Outs By Category */}
        <div
          className={
            mode === 'dueout'
              ? 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch my-2'
              : roleContext.isCSS
              ? 'col-sm-12 col-md-6 d-flex align-items-stretch d-none my-2'
              : 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch d-none my-2'
          }
        >
          <div className='shadow card w-100'>
            <div className='d-flex flex-column card-body h-100'>
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex mb-1 title-border'>
                  <h2
                    aria-label='By Category'
                    className='poppins-bold font-14 title-color20'
                    tabIndex={0}
                  >
                    By Category
                    <span className='badge bgcolor-primary border-radius3 px-2 py-1 mx-1 font-12 whitetext poppins-semibold'>
                      {defaultToEmptyString(twoDigits(dueOutCount))}
                    </span>
                  </h2>
                </div>
                <div>
                  {dueOutCount > 0 && (
                    <Link
                      className='links-over-underline font-12 links'
                      onClick={() => setKPIClicked(true)}
                      title='View All'
                      to={`${ROUTE_TYPE}dueouts`}
                    >
                      View All
                    </Link>
                  )}
                  {inquiryFilter.categoryCodeFilter !== -1 && (
                    <Link
                      className='links-over-underline font-12 links mx-2 border-left1 ps-2'
                      onClick={() =>
                        setInquiryFilter({
                          ...inquiryFilter,
                          categoryCodeFilter: -1,
                        })
                      }
                      title='Reset Filter'
                      to='#'
                    >
                      Reset Filter
                    </Link>
                  )}
                </div>
              </div>
              <div className='min-h-200 d-flex align-items-center justify-content-around'>
                {dueoutCategoryCounts?.length > 0 ? (
                  <ByCategoryChart
                    DueOutsByCategory={dueoutCategoryCounts}
                    categories={categories}
                    isDueOut={true}
                  />
                ) : (
                  <div
                    aria-label={NORESULT}
                    className='subtitle-color2 montserratregular'
                    tabIndex={0}
                  >
                    {NORESULT}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Due Outs By Categor End */}
        <div
          className={
            mode === 'dueout'
              ? 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch d-none my-2'
              : roleContext.isCSS
              ? 'col-sm-12 col-md-6 d-flex align-items-stretch my-2'
              : 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch my-2'
          }
        >
          <div className='shadow card w-100'>
            <div className='d-flex flex-column card-body h-100'>
              <div className='d-flex justify-content-between align-items-center flex-wrap'>
                <div className='d-flex mb-1 title-border'>
                  <h2
                    aria-label='Inquiries By Category'
                    className='poppins-bold font-14 title-color20'
                    tabIndex={0}
                  >
                    Inquiries By Category{' '}
                    <span className='badge bgcolor-primary border-radius3 px-2 py-1 font-12 whitetext poppins-semibold'>
                      {defaultToEmptyString(twoDigits(categoryTotal))}
                    </span>
                  </h2>
                </div>
                <div className='ms-md-auto'>
                  {categoryTotal > 0 && (
                    <Link
                      className='links-over-underline font-12 links'
                      title='View All'
                      to={`${ROUTE_TYPE}inquiries`}
                    >
                      View All
                    </Link>
                  )}
                  {inquiryFilter.categoryCodeFilter !== -1 && (
                    <Link
                      className='links-over-underline font-12 links mx-2 border-left1 ps-2'
                      onClick={() =>
                        setInquiryFilter({
                          ...inquiryFilter,
                          categoryCodeFilter: -1,
                        })
                      }
                      title='Reset Filter'
                      to='#'
                    >
                      Reset Filter
                    </Link>
                  )}
                </div>
              </div>
              <div className='min-h-200 d-flex align-items-center justify-content-around'>
                {inquiryCategoryCounts.length > 0 ? (
                  <ByCategoryChart
                    categories={categories}
                    inquiryCategoryCounts={inquiryCategoryCounts}
                  />
                ) : (
                  <div
                    aria-label={NORESULT}
                    className='subtitle-color2 montserratregular'
                    tabIndex={0}
                  >
                    {NORESULT}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className={
            mode === 'dueout'
              ? 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch d-none my-2'
              : roleContext.isCSS
              ? 'col-sm-12 col-md-6 d-flex align-items-stretch my-2'
              : 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch my-2'
          }
        >
          <div className='shadow card w-100'>
            <div className='d-flex flex-column card-body h-100'>
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex mb-1 title-border'>
                  <h2
                    aria-label=' By Status'
                    className='poppins-bold font-14 title-color20'
                    tabIndex={0}
                  >
                    By Status
                    <span className='badge bgcolor-primary border-radius3 px-2 py-1  mx-1 font-12 whitetext poppins-semibold'>
                      {defaultToEmptyString(twoDigits(statusCount))}
                    </span>
                  </h2>
                </div>
                <div>
                  {statusCount > 0 && (
                    <Link
                      className='links-over-underline font-12 links'
                      title='View All'
                      to={`inquiries`}
                    >
                      View All
                    </Link>
                  )}
                  <Link
                    className='links-over-underline font-12 links mx-2 border-left1 ps-2 d-none'
                    title='Reset Filter'
                    to='#'
                  >
                    Reset Filter
                  </Link>
                </div>
              </div>
              <div className='min-h-200 d-flex align-items-center justify-content-around'>
                {statusCount > 0 ? (
                  <ByStatusChart
                    formattedData={[
                      {
                        fill: '#7E04B6',
                        name: 'Submitted',
                        value: statusCounts.submittedInquiryCount,
                      },
                      {
                        fill: '#152350',
                        name: 'Reopened',
                        value: statusCounts.reopenedInquiryCount,
                      },
                      {
                        fill: '#3E6B84',
                        name: 'CSS',
                        value: statusCounts.cssInquiryCount,
                      },
                      {
                        fill: '#4240BE',
                        name: 'Technician',
                        value: statusCounts.technicianInquiryCount,
                      },
                      {
                        fill: '#407026',
                        name: 'Supervisor',
                        value: statusCounts.supervisorInquiryCount,
                      },
                      {
                        fill: '#C17117',
                        name: 'Section Chief',
                        value: statusCounts.sectionChiefInquiryCount,
                      },
                      {
                        fill: '#610050',
                        name: 'MPF Leadership',
                        value: statusCounts.leadershipInquiryCount,
                      },
                    ]}
                  />
                ) : (
                  <div
                    aria-label={NORESULT}
                    className='subtitle-color2 montserratregular'
                    tabIndex={0}
                  >
                    {NORESULT}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Due Outs By Status */}
        <div
          className={
            mode === 'dueout'
              ? 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch my-2'
              : 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch d-none my-2'
          }
        >
          <div className='shadow card w-100'>
            <div className='d-flex flex-column card-body h-100'>
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex mb-1 title-border'>
                  <h2
                    aria-label=' By Status'
                    className='poppins-bold font-14 title-color20'
                    tabIndex={0}
                  >
                    By Status
                    <span className='badge bgcolor-primary border-radius3 px-2 py-1 mx-1 font-12 whitetext poppins-semibold'>
                      {defaultToEmptyString(twoDigits(dueOutCount))}
                    </span>
                  </h2>
                </div>
                <div>
                  {dueOutCount > 0 && (
                    <Link
                      className='links-over-underline font-12 links'
                      onClick={() => setKPIClicked(true)}
                      title='View All'
                      to={`${ROUTE_TYPE}dueouts`}
                    >
                      View All
                    </Link>
                  )}
                  <Link
                    className='links-over-underline font-12 links mx-2 border-left1 ps-2 d-none'
                    title='Reset Filter'
                    to='#'
                  >
                    Reset Filter
                  </Link>
                </div>
              </div>
              <div className='min-h-200 d-flex align-items-center justify-content-around'>
                {dueOutStatusCount?.length > 0 ? (
                  <ByStatusChart
                    formattedData={[
                      {
                        fill: '#7E04B6',
                        name: 'Completed',
                        value: getDueOutGUIDCount('COMPLETED'),
                      },
                      {
                        fill: '#152350',
                        name: 'In Progress',
                        value: getDueOutGUIDCount('IN_PROGRESS'),
                      },
                      {
                        fill: '#3E6B84',
                        name: 'Not Started',
                        value: getDueOutGUIDCount('NOT_STARTED'),
                      },
                    ]}
                  />
                ) : (
                  <div
                    aria-label={NORESULT}
                    className='subtitle-color2 montserratregular'
                    tabIndex={0}
                  >
                    {NORESULT}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Due Outs By Status */}

        {/* Due Outs By Priority */}
        <div
          className={
            mode === 'dueout'
              ? 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch my-2'
              : 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch d-none my-2'
          }
        >
          <div className='shadow card w-100'>
            <div className='d-flex flex-column card-body h-100'>
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex mb-1 title-border'>
                  <h2
                    aria-label=' By Priority'
                    className='poppins-bold font-14 title-color20'
                    tabIndex={0}
                  >
                    By Priority{' '}
                    <span className='badge bgcolor-primary border-radius3 px-2 py-1 font-12 whitetext poppins-semibold'>
                      {defaultToEmptyString(twoDigits(dueOutCount))}
                    </span>
                  </h2>
                </div>
                <div>
                  {dueOutCount > 0 && (
                    <Link
                      className='links-over-underline font-12 links'
                      onClick={() => setKPIClicked(true)}
                      title='View All'
                      to={`${ROUTE_TYPE}dueouts`}
                    >
                      View All
                    </Link>
                  )}
                  <Link
                    className='links-over-underline font-12 links mx-2 border-left1 ps-2 d-none'
                    title='Reset Filter'
                    to='#'
                  >
                    Reset Filter
                  </Link>
                </div>
              </div>
              <div className='min-h-200 d-flex align-items-center justify-content-around'>
                {dueOutCount > 0 ? (
                  <ByPieChart
                    dueoutHighPriority={getDueOutGUIDPriorityCount('High')}
                    dueoutNormalPriority={getDueOutGUIDPriorityCount('Normal')}
                    inquirySourceCSSCount={0}
                    inquirySourceCustomerCount={dueOutCount}
                    isDueout={true}
                  />
                ) : (
                  <div
                    aria-label={NORESULT}
                    className='subtitle-color2 montserratregular'
                    tabIndex={0}
                  >
                    {NORESULT}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Due Outs By Priority */}
        <div
          className={
            mode === 'dueout'
              ? 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch d-none my-2'
              : 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch my-2'
          }
        >
          <div className='shadow card w-100'>
            <div className='d-flex flex-column card-body h-100'>
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex mb-1 title-border'>
                  <h2
                    aria-label=' By Action pending on inquiries'
                    className='poppins-bold font-14 title-color20'
                    tabIndex={0}
                  >
                    By Action pending on inquiries
                  </h2>
                </div>
                <div>
                  {pendingActionCount + customerActionCount > 0 && (
                    <Link
                      className='links-over-underline font-12 links'
                      title='View All'
                      to={`${ROUTE_TYPE}inquiries`}
                    >
                      View All
                    </Link>
                  )}
                  <Link
                    className='links-over-underline font-12 links mx-2 border-left1 ps-2 d-none'
                    title='Reset Filter'
                    to='#'
                  >
                    Reset Filter
                  </Link>
                </div>
              </div>
              <div className='min-h-200 d-flex align-items-center justify-content-around'>
                {pendingActionCount + customerActionCount > 0 ? (
                  <ActionPendingChart
                    inquiryCustomer={inquiryCustomer}
                    inquiryPending={inquiryPending}
                  />
                ) : (
                  <div
                    aria-label={NORESULT}
                    className='subtitle-color2 montserratregular'
                    tabIndex={0}
                  >
                    {NORESULT}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Due Outs By Due Date */}
        <div
          className={
            mode === 'dueout'
              ? 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch my-2'
              : 'col-sm-12 col-md-6 col-xl-4 d-flex align-items-stretch d-none my-2'
          }
        >
          <div className='shadow card w-100'>
            <div className='d-flex flex-column card-body h-100'>
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex mb-1 title-border'>
                  <h2
                    aria-label='By Due Date'
                    className='poppins-bold font-14 title-color20'
                    tabIndex={0}
                  >
                    By Due Date{' '}
                    <span className='badge bgcolor-primary border-radius3 px-2 py-1 font-12 whitetext poppins-semibold'>
                      {defaultToEmptyString(twoDigits(overdueCount))}
                    </span>
                  </h2>
                </div>
                <div></div>
              </div>
              <div className='min-h-200 d-flex align-items-center justify-content-around'>
                {overdueCount > 0 && (
                  <ByCategoryChart
                    DueOutsByCategory={dueDateResult}
                    categories={categories}
                    isDueOut={true}
                    isDuedate={true}
                  />
                )}
                {overdueCount === 0 && (
                  <div
                    aria-label={NORESULT}
                    className='subtitle-color2 montserratregular'
                    tabIndex={0}
                  >
                    {NORESULT}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Due Outs BBy Due Date */}
        <div
          className={
            mode === 'dueout'
              ? 'col-sm-12 col-md-6 col-xl-8 d-flex align-items-stretch d-none my-2'
              : 'col-sm-12 col-md-6 col-xl-8 d-flex align-items-stretch my-2'
          }
        >
          <div className='shadow card w-100'>
            <div className='d-flex flex-column card-body h-100 w-100'>
              <div className='d-flex  title-border d-flex justify-content-between align-items-center'>
                <div
                  aria-label='Totals By Month'
                  className='poppins-bold font-14 title-color20'
                  tabIndex={0}
                >
                  Totals By Month
                </div>
                <ul style={{ display: 'flex' }}>
                  <li
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      listStyle: 'none',
                      margin: 0,
                      marginRight: '5px',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: '#7E04B6',
                        borderRadius: '50%',
                        height: 10,
                        marginRight: 5,
                        width: 10,
                      }}
                    ></div>
                    <div
                      className='piechartlegend'
                      style={{
                        color: '#2c2929',
                        fontFamily: 'Lato-Semibold, Lato',
                        fontSize: 11,
                      }}
                    >
                      Submitted
                    </div>
                  </li>
                  <li
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      listStyle: 'none',
                      margin: 0,
                      marginRight: '5px',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: '#477A67',
                        borderRadius: '50%',
                        height: 10,
                        marginRight: 5,
                        width: 10,
                      }}
                    ></div>
                    <div
                      className='piechartlegend'
                      style={{
                        color: '#2c2929',
                        fontFamily: 'Lato-Semibold, Lato',
                        fontSize: 11,
                      }}
                    >
                      Resolved
                    </div>
                  </li>
                  <li
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      listStyle: 'none',
                      margin: 0,
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: '#5896B8',
                        borderRadius: '50%',
                        height: 10,
                        marginRight: 5,
                        width: 10,
                      }}
                    ></div>
                    <div
                      className='piechartlegend'
                      style={{
                        color: '#2c2929',
                        fontFamily: 'Lato-Semibold, Lato',
                        fontSize: 11,
                      }}
                    >
                      Reopened
                    </div>
                  </li>
                </ul>
              </div>
              <div className='min-h-200 w-100 d-flex align-items-center justify-content-around'>
                <TotalsByMonthChart
                  inquiryFilter={inquiryFilter}
                  totalByMonthCounts={totalByMonthCounts}
                />
              </div>
            </div>
          </div>
        </div>
        {/* Due Outs Table */}
        <div
          className={
            mode === 'dueout'
              ? 'col-sm-12 col-md-6 col-xl-8 d-flex align-items-stretch my-2'
              : 'col-sm-12 col-md-6 col-xl-8 d-flex align-items-stretch d-none my-2'
          }
        >
          <div className='shadow card w-100'>
            <div className='d-flex flex-column card-body h-100'>
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex mb-1 title-border'>
                  <div
                    aria-label='Due Outs'
                    className='poppins-bold font-14 title-color20'
                    tabIndex={0}
                  >
                    Due Outs{' '}
                    <span className='badge bgcolor-primary border-radius3 px-2 py-1 font-12 whitetext poppins-semibold'>
                      {defaultToEmptyString(twoDigits(kpiFilteredDueOutCount))}
                    </span>
                  </div>
                </div>
                <div></div>
              </div>
              <div className='d-flex align-items-center justify-content-around'>
                {accumulatedDueOuts.length > 0 ? (
                  <div
                    className='w-100 overflow-auto homedueoutstable'
                    onScroll={(e) => handleScroll(e, onTopScrollReached, handleBottomScrollReached)}
                  >
                    <DueoutsTable dueOuts={accumulatedDueOuts} showCheck={false} />
                  </div>
                ) : (
                  <div
                    aria-label={NORESULT}
                    className='subtitle-color2 montserratregular'
                    tabIndex={0}
                  >
                    {NORESULT}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Due Outs Table */}
      </div>
    </>
  )
}
export default AdminDashBoardTabContent
